/**
 * The two ways out of Einon pirtti - the window and the corridor - and the
 * takapiha the window drops into.
 *
 * The pane's box and which way it faces are hashed, and so is what putting a
 * fist through it costs. Where the shards land is not.
 */

import { decor, ext, solid, type Box, type DecorSpec, type SolidSpec } from '../world/Level';
import { T, WALL_H } from './levelDimensions';
import { BED, MATTRESS_TOP } from './levelMakuuhuone';

/* -------------------------------------------------- pirtin ikkuna ------- */

/**
 * ==========================================================================
 *  THE WINDOW, AND WHY THE FIRST SECOND IS THE HARD ONE
 * ==========================================================================
 *  The pirtti has two ways out of it. The door into the käytävä is one, and
 *  everybody takes it. The other is this: the south window, open since last
 *  night for the reason the ampari by the bed is open, with its lower sash
 *  lifted out and lying on the grass underneath.
 *
 *  It is a hole 2.0 m wide with its sill at `sill` and its head at `head`, and
 *  the numbers are the whole trick:
 *
 *    - the feet have to be over 0.50 m, and the jump apex is 0.71 m, so there
 *      is a 0.28 s window to be inside - almost exactly the 0.31 s the
 *      bemari's charging cable gives you at the other end of the run;
 *    - the head has to stay under 2.50 m, i.e. the feet under 0.78 m, which
 *      the apex clears by 7 cm and nothing else in the game does;
 *    - the *centre* has to travel 0.98 m to get from inside to outside (the
 *      wall's 0.30 m plus the player's own 0.68 m), so a shallow dive along
 *      the wall does not make it through. You have to square up to it, and
 *      squaring up costs you the line you were running.
 *
 *  Which is the point. This is the first second of the run, and the first
 *  second is the one part of it where a hop is nearly free: `momentum` starts
 *  at `MOMENTUM.min` and `breakMomentum` clamps there, so a jump before the
 *  meter has climbed costs the 7 % velocity bite and nothing else. By the lot
 *  the same jump costs a fifth of a meter that takes three seconds to earn
 *  back. So the technical jumping belongs *here*, at the start, and this is
 *  what it is for.
 *
 *  What it buys is the whole käytävä: five obstacles, two doors and 23 m of
 *  weaving between walls three metres apart, traded for a bend and a gate on
 *  open grass, where a stumble costs nothing and the meter climbs through both.
 *  Botilla: 6.88 s to the mouth of the lot against the door's 7.10 s, and a
 *  meter of 1.39 against 1.27. What it costs is a squared-up approach out of a
 *  spawn that is facing the other door, and a miss - clip the sill and you are
 *  standing still in a bedroom.
 *
 *  Both lines cross `PIRTTI_EXIT`, which is why that checkpoint is a gate
 *  across the whole end of the building rather than a box in the doorway. See
 *  `checkpoints`.
 * ==========================================================================
 */
export const IKKUNA = {
  /** The opening in the south wall. Wide enough to dive at, not to stroll in. */
  minX: 7.0,
  maxX: 9.0,
  /** Top of the sill: what the feet have to clear. */
  sill: 0.5,
  /** Underside of the header: what the head has to stay under. */
  head: 2.5,
  /**
   * How far in from the outer face the sash is hung, and how thick it is. A
   * window sits in the middle of the reveal rather than flush with the boards,
   * which is what gives the opening its depth from outside.
   */
  sashInset: 0.11,
  sashDepth: 0.06,
  /** Width of the sash's own rails and stiles: painted wood round the glass. */
  sashWidth: 0.07,
} as const;

/** Outer and inner faces of the pirtti's south wall. */
export const ETELASEINA = { outer: -6.3, inner: -6.0 } as const;

/** Where the sash hangs in the reveal, front and back. */
const PUITE_Z = {
  front: ETELASEINA.outer + IKKUNA.sashInset,
  back: ETELASEINA.outer + IKKUNA.sashInset + IKKUNA.sashDepth,
} as const;

/**
 * The glass, and the fact that it is still in the window.
 *
 * The lower sash used to be lying in the grass under its own window, which was
 * a way of saying "this hole is open" without asking the renderer to draw
 * anything you could see through. It is back in the frame now, glazed, and the
 * run goes through it - see `IkkunaSpec` in src/world/Level.ts for why a pane
 * that breaks costs no leaderboard time, and src/world/Ikkuna.ts for what
 * breaks it.
 *
 * Sized off the sash rather than off the opening: a pane is what the frame
 * leaves, and deriving it here is what stops the two from drifting apart.
 */
export const IKKUNALASI = ((): Box => {
  const { minX, maxX, sill, head, sashWidth, sashDepth } = IKKUNA;
  // Glass is a millimetre or two of nothing; drawn at 14 mm because a sheet
  // thinner than that z-fights its own frame from the far end of the takapiha.
  const thickness = 0.014;
  const front = PUITE_Z.front + (sashDepth - thickness) / 2;
  return {
    minX: minX + sashWidth,
    maxX: maxX - sashWidth,
    minY: sill + sashWidth,
    maxY: head - sashWidth,
    minZ: front,
    maxZ: front + thickness,
  };
})();

/**
 * The south wall in four pieces: two lengths of wall, the sill under the
 * opening and the header over it.
 *
 * Split here rather than typed out four times so the hole cannot drift away
 * from the frame drawn around it - `pirtinIkkuna()` reads the same numbers.
 */
export function pirtinEtelaseina(): SolidSpec[] {
  const { outer, inner } = ETELASEINA;
  return [
    solid('pirtti-seina-etela-a', 'pirttiWall', -T, outer, IKKUNA.minX, inner, WALL_H),
    solid('pirtti-seina-etela-b', 'pirttiWall', IKKUNA.maxX, outer, 10.3, inner, WALL_H),
    // The sill. Low enough to clear at the top of a jump, high enough that
    // walking at it just stops you - collision has no step-up.
    solid('pirtti-seina-ikkunapenkki', 'pirttiWall', IKKUNA.minX, outer, IKKUNA.maxX, inner, IKKUNA.sill),
    // The header, which is what stops the opening being a doorway.
    solid(
      'pirtti-seina-ikkunan-kamana',
      'pirttiWall',
      IKKUNA.minX,
      outer,
      IKKUNA.maxX,
      inner,
      WALL_H - IKKUNA.head,
      IKKUNA.head,
    ),
  ];
}

/**
 * What makes the hole read as a window: a white frame around it on the
 * facade, and a glazed sash hung in the reveal behind that.
 *
 * All decor, and the honesty note is the whole of it. The pielet outside the
 * wall are what they always were: every millimetre of them is clear of the
 * opening, because a frame you can run into that is not where it looks is
 * exactly the lie the ansalanka's cable is written not to tell.
 *
 * The sash and its glass are the other case, and they are the reason that rule
 * has to be stated rather than obeyed. They stand *inside* the opening and
 * neither of them is solid - which would be the same lie, told better, if the
 * glass did not break. It does, on the frame the runner crosses the wall, with
 * the sound and the shards to say so. What is left standing afterwards is a
 * 70 mm painted lip round the edge of a two-metre hole, which is the one shape
 * that can be drawn inside a collider without moving what fits through it.
 *
 * The sash is a single light on purpose. A pirtti window this size would have
 * a glazing bar across the middle, and a glazing bar is wood: it would still
 * be there after the glass went, hanging across the opening at chest height,
 * telling the runner the hole is smaller than it is. The pane goes; nothing
 * that stays is in the way.
 */
export function pirtinIkkuna(): DecorSpec[] {
  const face = ETELASEINA.outer;
  const jamb = 0.09;
  const { minX, maxX, sill, head, sashWidth } = IKKUNA;
  const { front, back } = PUITE_Z;
  /** One length of the sash, given in the same order as the opening's edges. */
  const puite = (
    name: string,
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
  ): DecorSpec => decor(name, 'porcelain', fromX, front, toX, back, toY - fromY, fromY);

  return [
    decor('pirtin-ikkuna-pieli-a', 'porcelain', minX - jamb, face - 0.05, minX, face, head - sill + 2 * jamb, sill - jamb),
    decor('pirtin-ikkuna-pieli-b', 'porcelain', maxX, face - 0.05, maxX + jamb, face, head - sill + 2 * jamb, sill - jamb),
    decor('pirtin-ikkuna-alapiel', 'porcelain', minX - jamb, face - 0.06, maxX + jamb, face, jamb, sill - jamb),
    decor('pirtin-ikkuna-ylapiel', 'porcelain', minX - jamb, face - 0.05, maxX + jamb, face, jamb, head),
    // The sash: two stiles and two rails, flush with the opening's own edges,
    // so the frame the eye measures the hole by *is* the collider's edge.
    puite('pirtin-ikkunapuite-a', minX, sill, minX + sashWidth, head),
    puite('pirtin-ikkunapuite-b', maxX - sashWidth, sill, maxX, head),
    puite('pirtin-ikkunapuite-ala', minX + sashWidth, sill, maxX - sashWidth, sill + sashWidth),
    puite('pirtin-ikkunapuite-yla', minX + sashWidth, head - sashWidth, maxX - sashWidth, head),
  ];
}

/* ------------------------------------------------ pirtin käytävä ------- */

export const KAYTAVA = { southZ: 0.4, northZ: 4.25 } as const;

/** The open bedroom arch and the two visible precision gates on the floor line. */
export const PIRTTI_ROUTE = {
  arch: { minZ: 0.5, maxZ: 2.9 },
  gap: 1.25,
  gates: [
    { name: 'kaytavan-varustelaatikot-1', x: 16.1, side: -1 as const },
    { name: 'kaytavan-varustelaatikot-2', x: 25.1, side: 1 as const },
  ],
  gateDepth: 0.6,
  gateHeight: 1.3,
} as const;

/** Single beds run along the walls; the floor between them carries momentum. */
export const KAYTAVAN_SANKY_WIDTH = BED.width;
const KAYTAVAN_SANGYT = [
  { name: 'kaytavan-sanky-1', from: 13.2, side: -1 },
  { name: 'kaytavan-sanky-2', from: 17.6, side: -1 },
  { name: 'kaytavan-sanky-3', from: 22.3, side: 1 },
  { name: 'kaytavan-sanky-4', from: 26.2, side: 1 },
];

export function kaytavanSankyBox(sanky: (typeof KAYTAVAN_SANGYT)[number]): Box {
  const minZ = sanky.side < 0 ? KAYTAVA.southZ : KAYTAVA.northZ - KAYTAVAN_SANKY_WIDTH;
  return ext(sanky.from, minZ, sanky.from + BED.length, minZ + KAYTAVAN_SANKY_WIDTH, MATTRESS_TOP);
}

export function kaytavanSangyt(): SolidSpec[] {
  return KAYTAVAN_SANGYT.map((sanky) => ({
    name: sanky.name,
    material: 'furniture',
    // The collider includes the mattress; decor draws the frame below it.
    shape: 'invisible',
    box: kaytavanSankyBox(sanky),
  }));
}

export function kaytavanSankyDecor(): DecorSpec[] {
  return KAYTAVAN_SANGYT.flatMap((sanky) => {
    const box = kaytavanSankyBox(sanky);
    const mattressBottom = box.maxY - BED.mattress;
    return [
      // Separate geometry avoids coplanar wood/fabric flicker at shallow angles.
      decor(sanky.name.replace('sanky', 'sangynrunko'), 'furniture',
        box.minX, box.minZ, box.maxX, box.maxZ, mattressBottom - box.minY, box.minY),
      decor(
        sanky.name.replace('sanky', 'patja'), 'fabric',
        box.minX + 0.08, box.minZ + 0.08, box.maxX - 0.08, box.maxZ - 0.08,
        BED.mattress, mattressBottom,
      ),
    ];
  });
}

/** Two stacks of LAN gear leave short, alternating gaps; both can be read ahead. */
export const KAYTAVAN_VARUSTEET = PIRTTI_ROUTE.gates.map((gate) => {
  const minZ = gate.side < 0 ? KAYTAVA.southZ : KAYTAVA.southZ + PIRTTI_ROUTE.gap;
  const maxZ = gate.side < 0 ? KAYTAVA.northZ - PIRTTI_ROUTE.gap : KAYTAVA.northZ;
  return solid(gate.name, 'metal', gate.x, minZ, gate.x + PIRTTI_ROUTE.gateDepth, maxZ, PIRTTI_ROUTE.gateHeight);
});

/** Dark seams make the blocking volume read as stacked equipment cases. */
export const KAYTAVAN_VARUSTEIDEN_SAUMAT = KAYTAVAN_VARUSTEET.flatMap(({ name, box }) =>
  [0.42, 0.85].map((height, i) => decor(
    `${name}-sauma-${i}`, 'tyre', box.minX - 0.005, box.minZ - 0.005,
    box.maxX + 0.005, box.maxZ + 0.005, 0.025, height,
  )),
);

/* --------------------------------------------------- pirtin takapiha --- */

/**
 * What is behind the pirtti, which until there was a window in its south wall
 * was nothing at all.
 *
 * The shape of it is the whole balance of the fork. Straight out of the window
 * to the gate at the end of the building is 26 m of grass, and 26 m of grass
 * with nothing in it would make the käytävä scenery. So two things stand on
 * that diagonal, and they deliberately ask different questions:
 *
 *   halkokatos   x 15.4 .. 19.0   the loose one. It is 3.6 m long and simply
 *                                 in the way; there is four metres of room
 *                                 past its north side, so what it costs is a
 *                                 bend committed to early rather than a line
 *                                 threaded late.
 *   klapipino    x 22.6 .. 26.6   the tight one. Stacked against the outside
 *                                 of the käytävä's own south wall, which is
 *                                 what turns the 2.6 m it leaves into a gate.
 *                                 Come off the shed's north side and you are
 *                                 already lined up for it.
 *
 * So the back yard is a bend and then a gate, one wall away from everybody who
 * took the door and close enough to hear them. South of both is open grass:
 * slower, and always there.
 *
 * The huussi is the third thing, and it is where you land rather than on the
 * diagonal - a dive that goes straight out instead of out and east puts it in
 * front of you.
 *
 * The rest is dressing and stands well off the line. This is a farmyard's back
 * side, not a second corridor: the point of the window is that it buys open
 * ground - a place where a scripted stumble costs nothing instead of costing a
 * wall - and open ground that turned out to be a corridor with a hedge in it
 * would be the game taking the trade back.
 */
export const TAKAPIHA_ESTEET: SolidSpec[] = [
  // Where a pirtti's huussi is: behind it, far enough from the wall to walk
  // round, close enough that nobody carries a torch to it.
  solid('ulkohuussi', 'wood', 10.9, -6.2, 12.3, -4.8, 2.3),
  // The woodshed, straight across the diagonal. Nothing about it is tight -
  // it is 3.6 m long, so what it costs is a bend committed to early.
  solid('halkokatos', 'wood', 15.4, -6.7, 19.0, -4.3, 2.4),
  // The winter's wood that did not fit in the shed, stacked along the käytävä's
  // south wall - which is what makes the 2.6 m it leaves a gate rather than a
  // gap. Come off the shed's north side and you are already in it.
  solid('pirtin-klapipino', 'wood', 22.6, -5.2, 26.6, -2.5, 1.55),
  // Dressing, all of it off the line.
  solid('kompostori', 'furniture', 16.4, -11.4, 17.4, -10.4, 0.95),
  solid('pirtin-kottikarryt', 'metal', 28.6, -8.4, 30.2, -7.2, 0.85),
  solid('pyykkinaru-pirtti-a', 'wood', 21.4, -10.6, 21.8, -10.2, 2.2),
  solid('pyykkinaru-pirtti-b', 'wood', 28.6, -10.6, 29.0, -10.2, 2.2),
];
